/**
 * Pure link classification for section CTAs (no React).
 * Used by SectionConfigButton for router vs <a>, targets, and safe hrefs.
 */

export type SectionLinkKind =
    | "internal_path"
    | "internal_hash"
    | "external_http"
    | "protocol_relative"
    | "mailto"
    | "tel"
    | "whatsapp"
    | "invalid";

export interface SectionLinkClassification {
    kind: SectionLinkKind;
    /** When true, open in new tab unless `openInNewTab === false` on the button config. */
    openInNewTabDefault: boolean;
}

const WA_RE = /wa\.me\//i;

/** Normalize href; empty → "#" (safe no-op). */
export function sanitizeSectionButtonHref(link: unknown): string {
    if (link == null) return "#";
    const t = String(link).trim();
    return t.length > 0 ? t : "#";
}

export function classifySectionButtonLink(raw: string): SectionLinkClassification {
    const s = raw.trim();
    if (!s) {
        return { kind: "invalid", openInNewTabDefault: false };
    }
    if (s === "#" || s.startsWith("#")) {
        return { kind: "internal_hash", openInNewTabDefault: false };
    }
    if (s.startsWith("tel:")) {
        return { kind: "tel", openInNewTabDefault: false };
    }
    if (s.startsWith("mailto:")) {
        return { kind: "mailto", openInNewTabDefault: false };
    }
    if (WA_RE.test(s) || s.toLowerCase().includes("api.whatsapp.com")) {
        return { kind: "whatsapp", openInNewTabDefault: true };
    }
    if (s.startsWith("//")) {
        return { kind: "protocol_relative", openInNewTabDefault: true };
    }
    if (/^https?:\/\//i.test(s)) {
        return { kind: "external_http", openInNewTabDefault: true };
    }
    if (s.startsWith("/")) {
        return { kind: "internal_path", openInNewTabDefault: false };
    }
    // "shop" or "collections/foo" without scheme → internal only if slug-safe
    if (!s.includes(":") && !/\s/.test(s) && /^[a-zA-Z0-9/_-]+$/.test(s)) {
        return { kind: "internal_path", openInNewTabDefault: false };
    }
    return { kind: "invalid", openInNewTabDefault: false };
}

/**
 * Final href for DOM.
 * - Store-relative paths go through `getStorePath`.
 * - Hash / tel / mailto / external left as-is (or normalized).
 */
export function hrefForSectionButton(
    link: string,
    getStorePath: (path: string) => string
): string {
    const s = sanitizeSectionButtonHref(link);
    const { kind } = classifySectionButtonLink(s);

    if (kind === "invalid") {
        return "#";
    }
    if (kind === "internal_hash") {
        return s.startsWith("#") ? s : `#${s}`;
    }
    if (kind === "internal_path") {
        const path = s.startsWith("/") ? s : `/${s}`;
        return getStorePath(path);
    }
    return s;
}

/** Whether navigation should use in-app router (SPA) vs raw anchor. */
export function shouldUseRouterForSectionLink(link: string): boolean {
    const s = sanitizeSectionButtonHref(link);
    const { kind } = classifySectionButtonLink(s);
    return kind === "internal_path";
}

export function shouldOpenLinkInNewTab(
    link: string,
    openInNewTabConfig: boolean | undefined,
    classification: SectionLinkClassification
): boolean {
    if (openInNewTabConfig === false) return false;
    if (openInNewTabConfig === true) return true;
    return classification.openInNewTabDefault;
}

// --- Developer aliases (stable public API) ---
export const classifyLink = classifySectionButtonLink;
export const sanitizeHref = sanitizeSectionButtonHref;
